import { useEffect, useState } from 'react';
import { Typography, Grid } from "@mui/material";
import { ApiResponse } from "../models/ApiResponse";
import { ProductStageCheckListDetail } from "../models/ProductStageCheckListDetail";
import api from "../services/ApiService";
import { FormControl } from "@mui/base";
import ToggleSwitchButton from "./ToggleSwitchButton";
import moment from "moment";
import { DATE_TIME_FORMAT } from "../utils/constants";
import { Permissions } from "../models/Enum";
import SessionService from "../services/SessionService";
import { toast } from "react-toastify";

const StageCheckList = ({ checkListStatus, setCheckListStatus, isCheckListChanged, setIsCheckListChanged, baseCode, editItemId, isCopyMode }) => {

  const sessionService = SessionService.getInstance();

  const [stageChecklistFields, setStageChecklistFields] = useState<ProductStageCheckListDetail[]>([]);
  
  useEffect(() => {
    fetchChecklist();
  }, [editItemId]);

  useEffect(() => {
    if (isCheckListChanged) {
      fetchChecklist();
      setIsCheckListChanged(false);
    }
  }, [isCheckListChanged]);
  /**
   * This function is used to fetch stage check list data and set it to state
   */
  const fetchChecklist = async () => {
    try {
      const response = await api
        .get<ApiResponse<ProductStageCheckListDetail[]>>(`/CheckList/list/${editItemId}`);
      if (response.isSuccess) {
        let { data } = response;
        if(!isCopyMode) {
          setStageChecklistFields(data);
        }
        if(isCopyMode && checkListStatus?.length === 0) {
          const updatedData = data?.map((item) => {
            if(!item?.isBaseCodeLevel) {
              return {
                ...item,
                status: false,
                lastupdatedByDisplayText: null,
                lastUpdatedDate: null
              }
            } else {
              return item;
            }
          });
          setStageChecklistFields(updatedData);
          setCheckListStatus(updatedData.filter(item => (!!item?.status))?.map(item => {
            return {
              ...item,
              id: 0
            }
          }));
        }
      } else {
        throw new Error(response?.message);
      }
    } catch (error: any) {
      console.error(error?.message);
    }
  };

  const checkManageCheckListFieldPermission = () => {
    return !sessionService.hasPermission(Permissions.ManageStageAdvancementCheckList);
  };

  /**
   * This function is used to render toogle switch button field with timestamp label
   * @param checkListDetail - Stage Checklist Field Detail
   */
  const renderToggleButton = (checkListDetail: ProductStageCheckListDetail) => (
    <FormControl key={checkListDetail.stageCheckListId} style={{ margin: '5px 0', width: '100%' }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={5}>
          <Typography style={{ textTransform: 'capitalize' }}>
            {checkListDetail.friendlyName}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          <ToggleSwitchButton
            checked={checkListDetail.status}
            onChange={(e) => handleToggleChange(e, checkListDetail)}
            fieldName={checkListDetail.checkListItemName}
            readOnlyField={checkManageCheckListFieldPermission()}
          />
        </Grid>
        <Grid item xs={3}>
          {checkListDetail.lastUpdatedDate && (
            <Typography>
              {moment.utc(checkListDetail.lastUpdatedDate).local().format(DATE_TIME_FORMAT)}
            </Typography>
          )}
        </Grid>
      </Grid>
    </FormControl>
  );

  /**
   * This function is used to handle toggle button change
   * @param e - Event object of Toggle switch button
   * @param checkListDetail - Stage Checklist Field Detail
   */
  const handleToggleChange = (e, checkListDetail) => {
    if (checkListDetail.isBaseCodeLevel && !baseCode?.trim()) {
      return toast.warn("BaseCode is not present for this product. Please add BaseCode to modify the checklist.");
    }
    
    const updatedFieldStatus = stageChecklistFields.map((dataItem) =>
      dataItem.stageCheckListId === checkListDetail.stageCheckListId
        ? { ...dataItem, status: e.target.checked }
        : dataItem
    );
    setStageChecklistFields(updatedFieldStatus);

    // Find the updated checklist field and set it to checkListStatus state to submit it to API
    const findUpdatedFieldStatus = updatedFieldStatus.find(
      (dataItem) => dataItem.stageCheckListId === checkListDetail.stageCheckListId
    );
    if (findUpdatedFieldStatus) {
      const isRevertedStatus = checkListStatus.find(listItem => listItem?.stageCheckListId === findUpdatedFieldStatus?.stageCheckListId);
      const withoutUpdatedChecklist = checkListStatus.filter(
          (listItem) => listItem.stageCheckListId !== findUpdatedFieldStatus.stageCheckListId);
      const updatedProductStageCheckListStatus = isRevertedStatus 
        ? withoutUpdatedChecklist
        : [
        ...withoutUpdatedChecklist,
        {
          id: findUpdatedFieldStatus.id,
          stageCheckListId: findUpdatedFieldStatus.stageCheckListId,
          status: findUpdatedFieldStatus.status,
          isBaseCodeLevel: findUpdatedFieldStatus.isBaseCodeLevel,
        },
      ];
      setCheckListStatus(updatedProductStageCheckListStatus);
    }
  };

  /**
   * This function is used to render checklist fields with category section label
   */
  const renderChecklist = () => {
    const groupedData = stageChecklistFields.reduce((acc, cl) => {
      if (!acc[cl.stageDisplayText]) {
        acc[cl.stageDisplayText] = [];
      }
      acc[cl.stageDisplayText].push(cl);
      return acc;
    }, {});

    return Object.keys(groupedData).map((stage) => (
      <div className="checklist-container" key={stage}>
        <Typography className="checklist-header" variant="h6">{stage}</Typography>
        {groupedData[stage].sort((a, b) => a.sequenceNum - b.sequenceNum).map((cl) => (
          <div className="checklist-buttons" key={cl.stageCheckListId}>
            {renderToggleButton(cl)}
          </div>
        ))}
      </div>
    ));
  };

  return <>
    {stageChecklistFields?.length > 0 && renderChecklist()}
  </>
}

export default StageCheckList;